import { useAccount, useReadContracts } from 'wagmi'
import { ADDRESSES, IS_LIVE, ZERO_ADDR } from '@/lib/contracts'
import { ERC20_ABI } from '@/lib/erc20'
import savingsAbi from '@/lib/abis/SavingsVault.json'
import farmAbi from '@/lib/abis/HorizonEmissions.json'
import { POLL_SLOW, toNumber } from '@/lib/utils'
import { useVaultEmissionRates } from '@/hooks/useVaultEmissionRates'
import { useHorizonPriceUsd } from '@/hooks/useHorizonPriceUsd'

const VAULT = ADDRESSES.SAVINGS_hUSD
const hUSD = ADDRESSES.hUSD
const YEAR = 31_536_000

const NOT_DEPLOYED = !IS_LIVE || !VAULT || VAULT.toLowerCase() === ZERO_ADDR

/// shUSD — the hUSD savings account (hUSD-LAUNCH-SPEC.md). A single known ERC-4626 vault, so
/// this reads one address rather than enumerating a registry.
///
/// **Two returns, surfaced separately.** The share price rises in hUSD as `RevenueRouter` streams
/// the savings share of borrow interest in (organic, spread-funded), AND the shares earn the
/// time-boxed HORIZON bootstrap through the same farm table the ALM vaults use. The card shows both.
///
/// **The organic rate is read from the live stream, and labelled "current" not "APY".** The vault
/// releases each revenue push linearly over `streamDuration`, so `lockedProfit / streamDuration` is
/// the hUSD/second currently being recognised; annualised against `totalAssets` that is the current
/// organic yield. It is honest but spiky (it reflects the most recent push), so it is never presented
/// as a fixed promise. The HORIZON bootstrap is valued the same way the vaults page values emissions.
export function useSavingsHUsd() {
  const { address: user } = useAccount()
  const { horizonPerDayFor } = useVaultEmissionRates()
  const { rate: horizonUsd, found: hasHorizonPrice } = useHorizonPriceUsd()

  const { data: reads, refetch } = useReadContracts({
    query: { enabled: !NOT_DEPLOYED, refetchInterval: POLL_SLOW },
    contracts: [
      { address: VAULT, abi: savingsAbi, functionName: 'totalAssets' },
      { address: VAULT, abi: savingsAbi, functionName: 'totalSupply' },
      { address: VAULT, abi: savingsAbi, functionName: 'decimals' },
      { address: VAULT, abi: savingsAbi, functionName: 'depositsPaused' },
      { address: VAULT, abi: savingsAbi, functionName: 'lockedProfit' },
      { address: VAULT, abi: savingsAbi, functionName: 'streamDuration' },
      { address: ADDRESSES.HORIZON_EMISSIONS, abi: farmAbi, functionName: 'farmEndsAt', args: [VAULT] },
      { address: ADDRESSES.HORIZON_EMISSIONS, abi: farmAbi, functionName: 'isFarmActive', args: [VAULT] },
    ],
  })

  const { data: userReads, refetch: refetchUser } = useReadContracts({
    query: { enabled: !NOT_DEPLOYED && !!user, refetchInterval: POLL_SLOW },
    contracts: user
      ? [
          { address: VAULT, abi: savingsAbi, functionName: 'balanceOf', args: [user] },
          { address: hUSD, abi: ERC20_ABI, functionName: 'balanceOf', args: [user] },
          { address: hUSD, abi: ERC20_ABI, functionName: 'allowance', args: [user, VAULT] },
          { address: ADDRESSES.HORIZON_EMISSIONS, abi: farmAbi, functionName: 'pendingVaultHorizonOf', args: [VAULT, user] },
        ]
      : [],
  })

  const decimals = Number(reads?.[2]?.result ?? 18)
  const totalAssetsWei = reads?.[0]?.result ?? 0n
  const totalSupplyWei = reads?.[1]?.result ?? 0n
  const lockedWei = reads?.[4]?.result ?? 0n
  const streamDuration = Number(reads?.[5]?.result ?? 0n)
  const depositsPaused = !!reads?.[3]?.result
  const endsAt = Number(reads?.[6]?.result ?? 0n)
  const farmActive = !!reads?.[7]?.result

  const sharesWei = userReads?.[0]?.result ?? 0n
  const walletWei = userReads?.[1]?.result ?? 0n
  const allowance = userReads?.[2]?.result ?? 0n
  const pendingHorizon = toNumber(userReads?.[3]?.result ?? 0n)

  const totalAssets = toNumber(totalAssetsWei, decimals)
  const userAssetsWei = totalSupplyWei > 0n ? (sharesWei * totalAssetsWei) / totalSupplyWei : 0n
  const userAssets = toNumber(userAssetsWei, decimals)

  // Organic hUSD yield, annualised from the live release stream. hUSD is $1 by definition, so
  // totalAssets doubles as TVL in USD.
  const organicApy = streamDuration > 0 && totalAssets > 0
    ? (toNumber(lockedWei, decimals) / streamDuration * YEAR) / totalAssets
    : 0

  // HORIZON bootstrap, valued the same way the vaults page does (HORIZON/day × price ÷ TVL).
  const horizonPerDay = horizonPerDayFor ? horizonPerDayFor(VAULT) : null
  const bootstrapApy = horizonPerDay != null && hasHorizonPrice && totalAssets > 0
    ? (horizonPerDay * horizonUsd * 365) / totalAssets
    : 0

  function refetchAll() {
    refetch?.()
    refetchUser?.()
  }

  return {
    notDeployed: NOT_DEPLOYED,
    refetch: refetchAll,
    vault: {
      address: VAULT,
      asset: hUSD,
      decimals,
      depositsPaused,
      endsAt,
      farmActive,
      totalAssets,
      totalAssetsWei,
      sharePrice: totalSupplyWei > 0n ? Number(totalAssetsWei) / Number(totalSupplyWei) : 1,
      organicApy,
      bootstrapApy,
      totalApy: organicApy + bootstrapApy,
      horizonPerDay,
      horizonPerDayUsd: horizonPerDay != null && hasHorizonPrice ? horizonPerDay * horizonUsd : null,
      hasHorizonPrice,
      user: {
        shares: toNumber(sharesWei, decimals),
        sharesWei,
        assets: userAssets,
        assetsWei: userAssetsWei,
        wallet: toNumber(walletWei, decimals),
        walletWei,
        allowance,
        pendingHorizon,
        pendingHorizonUsd: hasHorizonPrice ? pendingHorizon * horizonUsd : null,
      },
    },
  }
}
